import React, { useEffect, useMemo, useState } from 'react';
import { loader } from 'graphql.macro';
import { get } from 'lodash';

import { MultiSelectDropDown, Option } from './MultiSelectDropDown';
import { SingleSelectDropDown } from './SingleSelectDropDown';
import { captureSentryException } from '../../helpers/sentryHelper';
import { Company } from '../../models/Common';
import { memberClient } from '../../config/Apollo';

interface Props {
  who5Filter?: string;
  matchedFilter?: string;
  companiesFilter?: string;
  hasUpcomingBooking?: string;
  hasRemainingSessions?: string;
  hasAppInstalled?: string;
  hasSelectedMembers?: boolean;
  onMatchStatusFilterChange: (value: string) => void;
  onWho5FilterChange: (value: string) => void;
  onCompanyFilterChange: (value: string) => void;
  setHasUpcomingBooking: (value: string) => void;
  setHasRemainingSessions: (value: string) => void;
  setHasAppInstalled: (value: string) => void;
  onClearAllFilters: () => void;
  showBulkMessageModal: () => void;
}

export enum ClientFilter {
  Matched = 'matchedFilter',
  HasUpcomingBooking = 'hasUpcomingBooking',
  HasRemainingSessions = 'hasRemainingSessions',
  Who5 = 'who5',
  HasAppInstalled = 'hasAppInstalled',
  Companies = 'companiesFilter',
}

export const ALL_CLIENT_FILTERS = [
  ClientFilter.Matched,
  ClientFilter.HasUpcomingBooking,
  ClientFilter.HasRemainingSessions,
  ClientFilter.Who5,
  ClientFilter.HasAppInstalled,
  ClientFilter.Companies,
];

export enum MatchStatus {
  NewMatch = 'New Match',
  Engaged = 'Engaged',
  Active = 'Active',
  Inactive = 'Inactive',
  Unmatched = 'Unmatched',
}

export enum Who5Options {
  Past7Days = 'Past 7 Days',
  WithinAMonth = 'Within a Month',
  MoreThanAMonth = 'More Than a Month',
  NotCompleted = 'Not Completed',
}

export enum Who5Values {
  Past7Days = '1',
  WithinAMonth = '2',
  MoreThanAMonth = '3',
  NotCompleted = '4',
}

export enum FilterSectionTestID {
  BulkMessageButton = 'BulkMessageButton',
  MatchedStatusMultiSelectFilter = 'MatchedStatusMultiSelectFilter',
  CompaniesFilter = 'CompaniesFilter',
  Who5Filter = 'Who5Filter',
  LastActiveFilter = 'LastActiveFilter',
  LastLoginFilter = 'LastLoginFilter',
  UpcomingBookingFilter = 'UpcomingBookingFilter',
  RemainingSessionsFilter = 'RemainingSessionsFilter',
  ClearAllButton = 'ClearAllButton',
  AppInstalledFilter = 'AppInstalledFilter',
}

export const InitialMatchStatusFilterSelection = [
  MatchStatus.NewMatch,
  MatchStatus.Engaged,
  MatchStatus.Active,
  MatchStatus.Inactive,
];

export const EmptyMatchStatusFilterSelection = [
  MatchStatus.NewMatch,
  MatchStatus.Engaged,
  MatchStatus.Active,
  MatchStatus.Inactive,
  MatchStatus.Unmatched,
];

const COMPANIES_QUERY = loader('./documents/CompaniesQuery.gql');

export const FilterSection: React.FC<Props> = ({
  onMatchStatusFilterChange,
  onCompanyFilterChange,
  onClearAllFilters,
  showBulkMessageModal,
  setHasUpcomingBooking,
  setHasRemainingSessions,
  onWho5FilterChange,
  setHasAppInstalled,
  matchedFilter,
  hasUpcomingBooking,
  hasRemainingSessions,
  hasAppInstalled,
  who5Filter,
  companiesFilter,
  hasSelectedMembers = false,
}) => {
  const [matchedActiveIndex, setMatchedActiveIndex] = useState(matchedFilter === '' ? 0 : 1);
  const [matchedActiveIndexes, setMatchedActiveIndexes] = useState<string[]>(
    matchedFilter ? matchedFilter.split(',') : [],
  );
  const [companyActiveItems, setCompanyActiveItems] = useState<string[]>(
    companiesFilter ? companiesFilter.split(',') : [],
  );
  const [upcomingBookingActiveItem, setUpcomingBookingActiveItem] = useState<number>(
    hasUpcomingBooking === '' ? -1 : hasUpcomingBooking === 'true' ? 0 : 1,
  );
  const [sessionsActiveItem, setSessionsActiveItem] = useState<number>(
    hasRemainingSessions === '' ? -1 : hasRemainingSessions === 'true' ? 0 : 1,
  );
  const [who5ActiveIndexes, setWho5ActiveIndexes] = useState<string[]>(
    who5Filter ? who5Filter.split(',') : [],
  );
  const [appInstalledActiveItem, setAppInstalledActiveItem] = useState<number>(
    hasAppInstalled === '' ? -1 : hasAppInstalled === 'true' ? 0 : 1,
  );

  // controls whether you can click the clear all filters button
  const [clearAllIsDisabled, setClearAllIsDisabled] = useState(true);

  useEffect(() => {
    if (
      matchedActiveIndex !== -1 ||
      matchedActiveIndexes.length > 0 ||
      who5ActiveIndexes.length > 0 ||
      companyActiveItems.length > 0 ||
      upcomingBookingActiveItem !== -1 ||
      sessionsActiveItem !== -1 ||
      appInstalledActiveItem !== -1
    ) {
      setClearAllIsDisabled(false);
      return;
    }
    setClearAllIsDisabled(true);
  }, [
    appInstalledActiveItem,
    sessionsActiveItem,
    upcomingBookingActiveItem,
    companyActiveItems,
    matchedActiveIndex,
    matchedActiveIndexes,
    who5ActiveIndexes,
  ]);

  const [companies, setCompanies] = useState<Option[]>([]);
  const [companySearchTerm, setCompanySearchTerm] = useState('');

  const updateCompanies = useMemo(
    () => async () => {
      try {
        const response = await memberClient.query({
          query: COMPANIES_QUERY,
          variables: {},
          fetchPolicy: 'network-only',
        });
        const data = get(response, 'data.companies');
        if (!data) {
          throw new Error(`No companies in the response: ${response}`);
        }
        const companyOpts = data.map((comp: Company) => {
          return { text: comp.name, value: comp.id };
        });
        setCompanies(companyOpts);
      } catch (e: any) {
        captureSentryException(e);
      }
    },
    [setCompanies],
  );

  const companyOptionsPresented: (searchTerm: string) => Option[] = (searchTerm) => {
    let filteredCompanies = companies;
    if (searchTerm.length > 0) {
      filteredCompanies = companies.filter((company) => {
        return company.text.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }
    return filteredCompanies;
  };

  useEffect(() => {
    updateCompanies();
  }, [updateCompanies]);

  return (
    <div className="flex flex-row items-center">
      <div className="mr-3 border-r-2 border-gray-300">
        <button
          onClick={showBulkMessageModal}
          className={`${
            hasSelectedMembers
              ? 'bg-blue-200 text-white hover:shadow cursor-pointer border-2 border-blue-200'
              : 'bg-white text-gray-500 border-2 border-gray-300 cursor-not-allowed'
          } rounded font-bold px-6 h-10 mr-3 whitespace-no-wrap`}
          disabled={!hasSelectedMembers}
          data-testid={FilterSectionTestID.BulkMessageButton}
        >
          Bulk Chat
        </button>
      </div>

      <MultiSelectDropDown
        dataTestID={FilterSectionTestID.CompaniesFilter}
        options={companyOptionsPresented(companySearchTerm)}
        placeholder="Company"
        activeIndexes={companyActiveItems}
        setActiveIndexes={(items) => setCompanyActiveItems(items)}
        onChange={() => onCompanyFilterChange(companyActiveItems.join(','))}
        onSearch={setCompanySearchTerm}
        onClear={() => {
          setCompanyActiveItems([]);
          setCompanySearchTerm('');
          onCompanyFilterChange('');
        }}
        searchTerm={companySearchTerm}
        showsSearch
      />

      <SingleSelectDropDown
        activeIndex={upcomingBookingActiveItem}
        dataTestID={FilterSectionTestID.UpcomingBookingFilter}
        onClear={() => setHasUpcomingBooking('')}
        onChange={setHasUpcomingBooking}
        options={[
          { text: 'Scheduled', value: 'true' },
          { text: 'Not Scheduled', value: 'false' },
        ]}
        placeholder="Next Booking Date"
        setActiveIndex={setUpcomingBookingActiveItem}
      />

      <MultiSelectDropDown
        options={[
          { text: MatchStatus.NewMatch, value: MatchStatus.NewMatch },
          { text: MatchStatus.Engaged, value: MatchStatus.Engaged },
          { text: MatchStatus.Active, value: MatchStatus.Active },
          { text: MatchStatus.Inactive, value: MatchStatus.Inactive },
          { text: MatchStatus.Unmatched, value: MatchStatus.Unmatched },
        ]}
        activeIndexes={matchedActiveIndexes}
        placeholder="Match Status"
        setActiveIndexes={setMatchedActiveIndexes}
        dataTestID={FilterSectionTestID.MatchedStatusMultiSelectFilter}
        onChange={() => onMatchStatusFilterChange(matchedActiveIndexes.join(','))}
        onClear={() => {
          setMatchedActiveIndexes([]);
          onMatchStatusFilterChange('');
        }}
      />

      <SingleSelectDropDown
        options={[
          { text: 'Available', value: 'true' },
          { text: 'None Left', value: 'false' },
        ]}
        placeholder="Sessions"
        activeIndex={sessionsActiveItem}
        setActiveIndex={setSessionsActiveItem}
        onClear={() => setHasRemainingSessions('')}
        onChange={setHasRemainingSessions}
        dataTestID={FilterSectionTestID.RemainingSessionsFilter}
      />

      <MultiSelectDropDown
        options={[
          {
            text: Who5Options.Past7Days,
            value: Who5Options.Past7Days,
          },
          {
            text: Who5Options.WithinAMonth,
            value: Who5Options.WithinAMonth,
          },
          {
            text: Who5Options.MoreThanAMonth,
            value: Who5Options.MoreThanAMonth,
          },
          {
            text: Who5Options.NotCompleted,
            value: Who5Options.NotCompleted,
          },
        ]}
        activeIndexes={who5ActiveIndexes}
        placeholder="Completed WHO-5 Score"
        setActiveIndexes={setWho5ActiveIndexes}
        onChange={() => onWho5FilterChange(who5ActiveIndexes.join(','))}
        onClear={() => {
          setWho5ActiveIndexes([]);
          onWho5FilterChange('');
        }}
        dataTestID={FilterSectionTestID.Who5Filter}
      />

      <SingleSelectDropDown
        options={[
          { text: 'Yes', value: 'true' },
          { text: 'No', value: 'false' },
        ]}
        placeholder="Chat Available"
        activeIndex={appInstalledActiveItem}
        setActiveIndex={setAppInstalledActiveItem}
        onClear={() => setHasAppInstalled('')}
        onChange={setHasAppInstalled}
        dataTestID={FilterSectionTestID.AppInstalledFilter}
      />

      {/* Clear All Filters looks a bit too high optically, so we're nudging it down a pixel here */}
      <button
        className={`flex ml-1 w-48 h-10 font-bold justify-center px-2 pb-2 pt-2.25 rounded whitespace-no-wrap ${
          clearAllIsDisabled
            ? 'text-gray-500 border border-gray-300 focus:outline-none cursor-not-allowed'
            : 'text-white border border-blue-200 bg-blue-200'
        } `}
        disabled={clearAllIsDisabled}
        onClick={() => {
          onClearAllFilters();
          setMatchedActiveIndex(-1);
          setMatchedActiveIndexes([]);
          setWho5ActiveIndexes([]);
          setCompanyActiveItems([]);
          setUpcomingBookingActiveItem(-1);
          setSessionsActiveItem(-1);
          setAppInstalledActiveItem(-1);
        }}
        data-testid={FilterSectionTestID.ClearAllButton}
      >
        Clear All Filters
      </button>
    </div>
  );
};
